import { createClient } from '@supabase/supabase-js';

// `import.meta.env` is injected by Vite in the browser build; plain Node (tests)
// has `import.meta` but no `env` — optional chaining keeps imports safe.
const RAW_URL = import.meta.env?.VITE_SUPABASE_URL;
const RAW_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY;

// Refs we know are dead / placeholder. Treat them as "not configured" so
// the UI can show a friendly "backend not connected" state rather than
// letting the browser bubble up a raw `TypeError: Failed to fetch` when
// the host doesn't resolve. Update this list if a project is ever
// renamed or replaced.
const KNOWN_DEAD_REFS = ['qvfeziwkrrftkmpxmymb'];

const looksDead = (url = '') =>
  KNOWN_DEAD_REFS.some((ref) => url.includes(ref));

// Accept either the legacy anon JWT (eyJ...) or the modern
// publishable key (sb_publishable_...). Both are safe in client code.
const looksLikeKey = (key = '') =>
  key.startsWith('sb_publishable_') || key.startsWith('eyJ');

const hasValidEnv =
  typeof RAW_URL === 'string' &&
  RAW_URL.startsWith('https://') &&
  typeof RAW_KEY === 'string' &&
  looksLikeKey(RAW_KEY) &&
  RAW_KEY.length > 20 &&
  !looksDead(RAW_URL);

export const isSupabaseConfigured = hasValidEnv;

if (!hasValidEnv) {
  // Loud, dev-friendly warning so this never silently breaks again.
  // eslint-disable-next-line no-console
  console.warn(
    '[supabase] Not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY ' +
    'in .env (project root) and restart the dev server. Until then, any DB call ' +
    'will be skipped and forms will show a friendly "backend not connected" message.'
  );
}

// We still create a client so imports don't crash. When env is missing we
// point it at an obviously-fake host so any accidental call fails fast and
// never silently hits a real (wrong) project. Components MUST guard with
// `isSupabaseConfigured` before calling.
const supabaseUrl = hasValidEnv ? RAW_URL : 'https://unconfigured.invalid';
const supabaseAnonKey = hasValidEnv
  ? RAW_KEY
  : 'unconfigured-anon-key-placeholder';

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

/**
 * Session-less auth client for sensitive flows (readiness OTP sign-in).
 * Nothing is persisted to localStorage — closing the tab or leaving the
 * session means signing in again with a fresh code. The distinct storageKey
 * silences the multiple-GoTrueClient warning.
 */
export const supabaseEphemeralAuth = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
    storageKey: 'lk-readiness-otp',
  },
});

/**
 * Translate any thrown value (Supabase error, fetch error, etc.) into a
 * short, user-safe message. The browser surfaces network failures
 * (DNS, offline, blocked, CORS-preflight-failed) as `TypeError: Failed to fetch`,
 * which is useless to a borrower — show something we can act on instead.
 */
export const friendlyErrorMessage = (err) => {
  if (!isSupabaseConfigured) {
    return 'Our backend isn\'t connected yet. Please email hello@loanknow.com and we\'ll add you manually.';
  }
  const msg = (err?.message || '').toLowerCase();
  if (
    err instanceof TypeError ||
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('network request failed') ||
    msg.includes('load failed')
  ) {
    return 'We couldn\'t reach our servers. Check your connection and try again — or email hello@loanknow.com.';
  }
  if (err?.code === '23505' || /duplicate/i.test(err?.message || '')) {
    return 'You\'re already on our list. We\'ll be in touch.';
  }
  return err?.message || 'Something went wrong. Please try again.';
};

export default supabase;
